// Aufsicht an der Kasse: Eine zweite Person gibt einen Schritt an Ort und Stelle frei – sie weist sich mit ihrer PIN
// aus, der Schritt läuft mit ihren Rechten, die Kassenkraft bleibt angemeldet (Kern: Verträge mit
// `secondPerson: true`, POST /api/actions/<name> mit `approval: { userId, pin }`).
//   pos.supervisor.list   wer an dieser Kasse freigeben kann (Personen mit PIN und Stornorecht am Standort)
//   pos.pin.status | set | clear   die EIGENE PIN – eine Aktion des Profils (die Profilseite gehört der Plattform)
// Die PIN ist ein Zugangsgeheimnis: nie für den Assistenten, nie im Audit-Text.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

export const PIN_HINT = 'Vier bis acht Ziffern, keine Zahlenreihe und nicht viermal dieselbe Ziffer.'

export const zPin = z
  .string({ error: 'Bitte geben Sie eine PIN ein.' })
  .trim()
  .regex(/^[0-9]{4,8}$/, { error: 'Die PIN besteht aus vier bis acht Ziffern.' })

export const supervisorList = defineContract({
  name: 'pos.supervisor.list',
  title: 'Aufsichten der Kasse anzeigen',
  description: 'Wer an dieser Kasse einen Schritt freigeben kann: Personen mit hinterlegter PIN und Stornorecht am Standort.',
  llm: {
    description:
      'Nennt die Personen, die an einer Kasse als Aufsicht freigeben können (Storno, Rabatt über der Grenze, ' +
      'Alterskontrolle nach einer Ablehnung): aktive Personen mit hinterlegter PIN und Stornorecht am Standort der ' +
      'Kasse, ohne die angemeldete Person selbst. Verändert nichts; eine PIN nennt die Aktion nie.',
    keywords: ['Aufsicht', 'Freigabe an der Kasse', 'wer darf stornieren'],
  },
  input: z.strictObject({ registerId: zId }),
  output: z.object({
    rows: z.array(z.object({ userId: zId, displayName: z.string(), /** PIN nach Fehlversuchen vorübergehend gesperrt */ locked: z.boolean() })),
    /** Hinweis, wenn niemand freigeben kann */
    hint: z.string().nullable(),
  }),
  permission: 'pos.sale.create',
  risk: 'read',
  scope: 'record',
})

const zPinStatus = z.object({
  /** ist eine PIN hinterlegt? */
  hasPin: z.boolean(),
  setAt: z.date().nullable(),
  /** nach Fehlversuchen gesperrt bis – null = frei */
  lockedUntil: z.date().nullable(),
  hint: z.string(),
})

export const pinStatus = defineContract({
  name: 'pos.pin.status',
  title: 'Eigene PIN für Freigaben anzeigen',
  description: 'Ob die angemeldete Person eine PIN für Freigaben hinterlegt hat und ob sie gerade gesperrt ist.',
  llm: {
    description: 'Sagt, ob die angemeldete Person eine PIN für Freigaben an der Kasse hinterlegt hat, seit wann, und ob sie nach Fehlversuchen gesperrt ist. Die PIN selbst ist nie lesbar.',
    keywords: ['PIN', 'Freigabe-PIN', 'Aufsicht'],
  },
  input: z.strictObject({}),
  output: zPinStatus,
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
  assistant: 'never',
})

export const pinSet = defineContract({
  name: 'pos.pin.set',
  title: 'Eigene PIN für Freigaben festlegen',
  description: 'Legt die PIN fest, mit der die angemeldete Person an der Kasse eines Kollegen freigibt – oder ersetzt sie.',
  llm: { description: 'Legt die eigene PIN für Freigaben fest. Nur für die Profilseite – nie für den Assistenten.', keywords: ['PIN festlegen'] },
  input: z.strictObject({ pin: zPin }),
  output: zPinStatus.extend({ message: z.string() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})

export const pinClear = defineContract({
  name: 'pos.pin.clear',
  title: 'Eigene PIN für Freigaben entfernen',
  description: 'Entfernt die PIN der angemeldeten Person; danach kann sie an der Kasse nicht mehr an Ort und Stelle freigeben.',
  llm: { description: 'Entfernt die eigene PIN für Freigaben. Nur für die Profilseite – nie für den Assistenten.', keywords: ['PIN entfernen'] },
  input: z.strictObject({}),
  output: zPinStatus.extend({ message: z.string() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})
